/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Button } from "./ui/button"
import { Bell, X, Check, AlertCircle, FileText, Calendar, User, Shield, Download } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Badge } from "./ui/badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip"
import { ScrollArea } from "./ui/scroll-area"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useAppStore } from "@/store/appStore"
import { usePwaInstall } from "@/hooks/usePwaInstall"

interface Notification {
  id: string
  type: "document" | "case" | "system" | "reminder" | "alert"
  title: string
  message: string
  timestamp: Date
  isRead: boolean
  priority: "low" | "medium" | "high"
  metadata?: {
    documentId?: string
    caseNumber?: string
    officerName?: string
    actionRequired?: boolean
  }
}

export function SiteHeader({ heading }: { heading?: string }) {
  const { _notifications } = useAppStore()
  const { isInstallable, promptInstall } = usePwaInstall()

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (_notifications?.data) {
      const processed = _notifications.data
        .slice(0, 20)
        .map((notification: any, index:number) => ({
          id: notification.id || `notif-${index}`,
          type: notification.type || "document",
          title: notification.title || "Notification",
          message: notification.message || "No message provided",
          timestamp: new Date(notification.createdAt),
          isRead: notification.isRead || false,
          priority: notification.title?.includes("high risk")
            ? "high"
            : notification.title?.includes("low risk")
            ? "low"
            : "medium",
          metadata: { actionRequired: true },
        }))
      setNotifications(processed)
    }
  }, [_notifications])

  const unreadCount = notifications.filter((n) => !n.isRead).length

  const markAsRead = (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)))
  }

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
  }

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  const getNotificationIcon = (type: Notification["type"]) => {
    switch (type) {
      case "document":
        return <FileText className="h-4 w-4" />
      case "case":
        return <Shield className="h-4 w-4" />
      case "reminder":
        return <Calendar className="h-4 w-4" />
      case "system":
        return <User className="h-4 w-4" />
      case "alert":
        return <AlertCircle className="h-4 w-4" />
      default:
        return <Bell className="h-4 w-4" />
    }
  }

  const getPriorityColor = (priority: Notification["priority"]) => {
    switch (priority) {
      case "high":
        return "border-l-red-500 bg-red-50 dark:bg-red-950/20"
      case "medium":
        return "border-l-yellow-500 bg-yellow-50 dark:bg-yellow-950/20"
      case "low":
        return "border-l-blue-500 bg-blue-50 dark:bg-blue-950/20"
      default:
        return "border-l-gray-500 bg-gray-50 dark:bg-gray-950/20"
    }
  }

  const formatTime = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return "Just now"
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mx-2 data-[orientation=vertical]:h-4" />
        <h1 className="text-base font-medium bg-gradient-to-br from-[#0d2846] via-[#0d2846]/80 to-[#e2af58] bg-clip-text text-transparent">{heading}</h1>
        <div className="ml-auto flex items-center gap-2">
          {isInstallable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="relative hidden sm:flex" onClick={promptInstall}>
                  <Download className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Install App</p>
              </TooltipContent>
            </Tooltip>
          )}
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="relative hidden sm:flex">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center"
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0" align="end">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-semibold text-lg">Notifications</h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">
                      Mark all read
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <ScrollArea className="h-96">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <Bell className="h-8 w-8 mb-2" />
                    <p>No notifications</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 border-l-4 transition-colors hover:bg-muted/50 ${
                          !notification.isRead ? "bg-muted/30" : ""
                        } ${getPriorityColor(notification.priority)}`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`p-2 rounded-full ${
                              notification.priority === "high"
                                ? "bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400"
                                : notification.priority === "medium"
                                  ? "bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400"
                                  : "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400"
                            }`}
                          >
                            {getNotificationIcon(notification.type)}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h4
                                className={`text-sm font-medium truncate ${
                                  !notification.isRead ? "text-foreground" : "text-muted-foreground"
                                }`}
                              >
                                {notification.title}
                              </h4>
                              <div className="flex items-center gap-1 ml-2">
                                {!notification.isRead && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeNotification(notification.id)}
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>

                            <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{notification.message}</p>

                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">
                                {formatTime(notification.timestamp)}
                              </span>

                              <div className="flex items-center gap-2">
                                {notification.metadata?.actionRequired && (
                                  <Badge variant="outline" className="text-xs">
                                    Action Required
                                  </Badge>
                                )}

                                {notification.metadata?.caseNumber && (
                                  <Badge variant="secondary" className="text-xs">
                                    {notification.metadata.caseNumber}
                                  </Badge>
                                )}

                                {!notification.isRead && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => markAsRead(notification.id)}
                                    className="h-6 px-2 text-xs"
                                  >
                                    <Check className="h-3 w-3 mr-1" />
                                    Mark read
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* {notifications.length > 0 && ( */}
                <div className="p-3 border-t bg-muted/30">
                  <Button variant="ghost" className="w-full text-sm" onClick={() => setIsOpen(false)}>
                    <Link to={'/notifications'}> View All Notifications</Link>
                  </Button>
                </div>
              {/* )} */}
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </header>
  )
}
